/**
 * Componente PIPControls
 * 
 * Controles de timer para a janela Picture-in-Picture
 * Permite pausar e retomar sessões de trabalho
 * Para finalizar a OS, o usuário deve ir na página web
 * 
 * @author VISION DESK
 * @version 0.28.10
 */

import React, { useState, useEffect } from 'react';
import { Play, Pause, Loader2, AlertCircle } from 'lucide-react';
import { useMutation } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { pauseSession, resumeSession } from '@/services/workSessionApi';
import type { PIPControlsProps } from '@/types/pictureInPicture';

export const PIPControls: React.FC<PIPControlsProps> = ({
  osId,
  sessionId,
  session,
  onRefresh,
  targetDocument,
}) => {
  // Estado para controlar contagem de 30 segundos após pausar
  const [secondsSincePause, setSecondsSincePause] = useState<number | null>(null);

  // Mutation para pausar sessão
  const pauseMutation = useMutation({
    mutationFn: () => pauseSession(osId, sessionId),
    onSuccess: () => {
      toast.success('⏸️ Sessão pausada');
      // Iniciar contagem de 30 segundos
      setSecondsSincePause(0);
      onRefresh();
    },
    onError: (error: any) => {
      // console.error('Erro ao pausar sessão:', error);
      toast.error(error.response?.data?.message || 'Erro ao pausar sessão');
    },
  });

  // Mutation para retomar sessão
  const resumeMutation = useMutation({
    mutationFn: () => resumeSession(osId, sessionId),
    onSuccess: () => {
      toast.success('▶️ Sessão retomada');
      // Resetar contagem ao retomar
      setSecondsSincePause(null);
      onRefresh();
    },
    onError: (error: any) => {
      // console.error('Erro ao retomar sessão:', error);
      toast.error(error.response?.data?.message || 'Erro ao retomar sessão');
    },
  });

  // Efeito para contar 30 segundos após pausar
  useEffect(() => {
    if (secondsSincePause !== null && secondsSincePause < 30) {
      const timer = setTimeout(() => {
        setSecondsSincePause(secondsSincePause + 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [secondsSincePause]);

  // Resetar contagem se sessão não estiver pausada
  useEffect(() => {
    if (!session?.isPaused) {
      setSecondsSincePause(null);
    }
  }, [session?.isPaused]);

  const handlePause = () => {
    if (session?.isActive && !session?.isPaused) {
      pauseMutation.mutate();
    }
  };

  const handleResume = () => {
    if (session?.isPaused) {
      resumeMutation.mutate();
    }
  };

  const isLoading = pauseMutation.isPending || resumeMutation.isPending;

  // Se sessão não está ativa, não mostrar controles
  if (!session?.isActive) {
    return (
      <div className="text-center py-4 text-gray-400 text-sm">
        Nenhuma sessão ativa
      </div>
    );
  }

  // Calcular tempo restante para poder finalizar na web
  const timeRemaining = secondsSincePause !== null ? Math.max(0, 30 - secondsSincePause) : null;
  const canFinishOnWeb = timeRemaining !== null && timeRemaining === 0;

  // Controles normais
  return (
    <div className="space-y-3">
      {/* Botão Pausar / Retomar */}
      {session?.isPaused ? (
        <button
          onClick={handleResume}
          disabled={isLoading}
          title="Retomar sessão"
          className="w-full py-3 px-4 bg-green-500/20 hover:bg-green-500/30 
                   text-green-400 rounded-lg transition-all duration-300
                   disabled:opacity-50 disabled:cursor-not-allowed
                   flex items-center justify-center gap-2 font-medium"
        >
          {resumeMutation.isPending ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : (
            <Play className="w-5 h-5" />
          )}
          Retomar
        </button>
      ) : (
        <button
          onClick={handlePause}
          disabled={isLoading}
          title="Pausar sessão"
          className="w-full py-3 px-4 bg-yellow-500/20 hover:bg-yellow-500/30 
                   text-yellow-400 rounded-lg transition-all duration-300
                   disabled:opacity-50 disabled:cursor-not-allowed
                   flex items-center justify-center gap-2 font-medium"
        >
          {pauseMutation.isPending ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : (
            <Pause className="w-5 h-5" />
          )}
          Pausar
        </button>
      )}

      {/* Mensagem informativa quando pausada */}
      {session?.isPaused && timeRemaining !== null && (
        <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-3">
          <div className="flex items-start gap-2">
            <AlertCircle className="w-4 h-4 text-orange-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1 text-xs">
              {canFinishOnWeb ? (
                <div>
                  <p className="text-orange-400 font-semibold mb-1">
                    ✅ Pronto para Finalizar!
                  </p>
                  <p className="text-gray-300">
                    Agora você pode ir na página web para finalizar esta OS.
                  </p>
                </div>
              ) : (
                <div>
                  <p className="text-orange-400 font-semibold mb-1">
                    ⏳ Aguarde {timeRemaining}s
                  </p>
                  <p className="text-gray-300">
                    Para finalizar a OS, aguarde a sincronização com a página web antes de parar.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
